import React, { useState } from 'react';
import { X, Archive, AlertTriangle, Trash2 } from 'lucide-react';
import { Lead } from '../types';

interface ArchiveLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, note: string) => Promise<void>;
  action: 'archive' | 'delete';
  lead: Lead | null;
}

const PREDEFINED_REASONS = [
  { value: 'spam_test', label: 'Spam o prueba' },
  { value: 'duplicate', label: 'Duplicado' },
  { value: 'no_budget', label: 'Sin presupuesto' },
  { value: 'wrong_timing', label: 'Timing incorrecto' },
  { value: 'competitor_won', label: 'Ganó la competencia' },
  { value: 'no_response', label: 'No responde (15+ días)' },
  { value: 'out_of_target', label: 'Fuera de target' },
  { value: 'requirements_mismatch', label: 'Requisitos no compatibles' },
  { value: 'other', label: 'Otro (especificar)' }
];

const ArchiveLeadModal: React.FC<ArchiveLeadModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  lead
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [note, setNote] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  if (!isOpen || !lead) return null;

  const deleting = action === 'delete';
  const title = deleting ? 'Eliminar lead' : 'Archivar lead';
  const ActionIcon = deleting ? Trash2 : Archive;
  const canConfirm = selectedReason && (selectedReason !== 'other' || note.trim());

  const handleConfirm = async () => {
    if (!canConfirm || isConfirming) return;

    setIsConfirming(true);
    try {
      await onConfirm(selectedReason, note.trim());
      handleClose();
    } catch {
      setIsConfirming(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setNote('');
    setIsConfirming(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ActionIcon className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {title}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isConfirming}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-orange-900 mb-1">
                  {deleting ? 'Eliminación definitiva' : 'Archivo del lead'}
                </h3>
                <p className="text-sm text-orange-700">
                  {deleting
                    ? 'Se eliminará el lead y se conservarán el motivo y la nota en el historial.'
                    : 'El lead se moverá a Leads Archivados y podrás restaurarlo más tarde.'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">Lead seleccionado</h3>
            <div className="space-y-1 text-sm">
              <p><span className="font-medium">Nombre:</span> {lead.fullName}</p>
              <p><span className="font-medium">Empresa:</span> {lead.company}</p>
              <p><span className="font-medium">Email:</span> {lead.email}</p>
              <p><span className="font-medium">Estado actual:</span> {lead.status}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo *
            </label>
            <select
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              disabled={isConfirming}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Selecciona una razón...</option>
              {PREDEFINED_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="lead-disposition-note" className="block text-sm font-medium text-gray-700 mb-2">
              {selectedReason === 'other' ? 'Nota explicativa *' : 'Nota explicativa (opcional)'}
            </label>
            <textarea
              id="lead-disposition-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isConfirming}
              maxLength={1000}
              rows={3}
              placeholder="Añade el contexto comercial sin incluir datos personales."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            disabled={isConfirming}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isConfirming || !canConfirm}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConfirming ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{deleting ? 'Eliminando...' : 'Archivando...'}</span>
              </>
            ) : (
              <>
                <ActionIcon className="w-4 h-4" />
                <span>{title}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArchiveLeadModal;
